import { configuration } from "./Configuration";
import { ApplicationError, UnauthorizedError } from "./general/Errors";
import { authenticatedFetch } from "./JWTAuthService";

const baseUrl = configuration.serverUrl;

/** What happened to one of the user's devices (tri-server `DevicePushResult`). */
export interface PushDeviceResult {
    installationId: string;
    deviceName?: string;
    appVersion?: string;
    status: "ok" | "error";
    error?: string;
}

export interface TestPushResponse {
    /** 0 = no device registered, not an error. */
    devices: number;
    results: PushDeviceResult[];
}

/** One device of the signed-in user (tri-server `GET /push/devices`); the Expo token is never part of it. */
export interface PushDevice {
    installationId: string;
    platform: "ios" | "android";
    appId: string;
    appVersion?: string;
    deviceName?: string;
    createdAt: string;
    lastSeenAt: string;
    /** false = switched off (token dead, or taken out of rotation by the server). */
    active: boolean;
    disabledReason?: string;
}

/** Throws the portal's usual errors for a failed answer; `ApplicationError.status` carries e.g. the 429 of the broadcast. */
const ensureOk = async (response: Response): Promise<Response> => {
    if (response.status === 401) {
        throw new UnauthorizedError();
    }
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new ApplicationError(body?.error ?? `${response.status} ${response.statusText}`, response.status);
    }

    return response;
};

const sendJson = (method: "POST" | "PUT", url: string, payload: unknown) =>
    authenticatedFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    }).then(ensureOk);

/** The signed-in user's own devices with the TRI app, newest first. */
export const listPushDevices = async (): Promise<PushDevice[]> => {
    const response = await ensureOk(await authenticatedFetch(`${baseUrl}/push/devices`));

    return (await response.json()).devices ?? [];
};

/**
 * Sends a test notification to the signed-in user's own devices; the server takes no recipient. Title and
 * text are optional (the alert editor sends a rule's rendered sample); without them the server's default text is used.
 */
export const sendTestPush = async (message?: { title?: string; body?: string }): Promise<TestPushResponse> =>
    (await sendJson("POST", `${baseUrl}/push/test`, message ?? {})).json();

/* ---------------------------------------------------------------------------------------------
 * Tenant administration and notification preferences (PUSH_NOTI.md section 11.3).
 * ------------------------------------------------------------------------------------------- */

/** One device of a tenant user as the administration sees it; the Expo token is never part of it. */
export interface TenantPushDevice {
    installationId: string;
    platform: "ios" | "android";
    deviceName?: string;
    appVersion?: string;
    lastSeenAt: string;
    active: boolean;
}

/** A user of the tenant with the push state of their devices (`GET /customers/:customer/push/users`). */
export interface TenantPushUser {
    userId: string;
    firstName?: string;
    lastName?: string;
    email: string;
    /** Role ids such as `customerAdministrator`. */
    roles: string[];
    /** At least one device is active. */
    pushActive: boolean;
    devices: TenantPushDevice[];
}

export interface PushSettings {
    /** Lock-screen text without alert details ("Neue Meldung in der TRI-App"). */
    neutralText: boolean;
}

export type DeliveryChannel = "push" | "sms";

/** One row of the delivery log (`GET /customers/:customer/push/deliveries`). */
export interface PushDelivery {
    id: string;
    at: string;
    rule?: string;
    asset?: string;
    userId: string;
    userName: string;
    channel: DeliveryChannel;
    /** `sent`, `receipt_ok`, `receipt_error`, `tap`, `acknowledged`, `escalated` or `skipped`. */
    event: string;
    detail?: string;
}

export interface PushDeliveryPage {
    items: PushDelivery[];
    nextCursor: string | null;
}

export interface BroadcastRequest {
    title: string;
    body: string;
    /** Left out = no restriction by role. Both left out = every user of the tenant. */
    roles?: string[];
    users?: string[];
}

export interface BroadcastResult {
    recipients: number;
    sent: number;
    noDevice: number;
}

export interface NotificationPreferences {
    types: { alert: boolean; task: boolean; defect: boolean; broadcast: boolean };
    quietHours: { enabled: boolean; from: string; to: string; timezone: string };
}

const customerUrl = (customer: string) => `${baseUrl}/customers/${encodeURIComponent(customer)}`;

/** All users of the tenant with their push state; source of the administration table and of the alert editor's recipient lists. */
export const listTenantPushUsers = async (customer: string): Promise<TenantPushUser[]> => {
    const response = await ensureOk(await authenticatedFetch(`${customerUrl(customer)}/push/users`));

    return (await response.json()).users ?? [];
};

export const getPushSettings = async (customer: string): Promise<PushSettings> => {
    const response = await ensureOk(await authenticatedFetch(`${customerUrl(customer)}/push/settings`));
    const body = await response.json();

    return { neutralText: body?.neutralText === true };
};

export const updatePushSettings = async (customer: string, settings: PushSettings): Promise<PushSettings> => {
    const response = await sendJson("PUT", `${customerUrl(customer)}/push/settings`, settings);
    const body = await response.json().catch(() => settings);

    return { neutralText: (body ?? settings).neutralText === true };
};

/** One page of the delivery log, newest first; pass the previous `nextCursor` for the next page. */
export const listPushDeliveries = async (
    customer: string,
    options: { cursor?: string | null; limit?: number } = {},
): Promise<PushDeliveryPage> => {
    const query = new URLSearchParams();

    if (options.cursor) query.set("cursor", options.cursor);
    if (options.limit) query.set("limit", String(options.limit));

    const suffix = query.toString() === "" ? "" : `?${query.toString()}`;
    const response = await ensureOk(await authenticatedFetch(`${customerUrl(customer)}/push/deliveries${suffix}`));
    const body = await response.json();

    return { items: body?.items ?? [], nextCursor: body?.nextCursor ?? null };
};

/** Sends a message to users of the tenant. The server allows five per hour and tenant and answers 429 beyond that. */
export const sendPushBroadcast = async (customer: string, request: BroadcastRequest): Promise<BroadcastResult> =>
    (await sendJson("POST", `${customerUrl(customer)}/push/broadcast`, request)).json();

export const getNotificationPreferences = async (): Promise<NotificationPreferences> => {
    const response = await ensureOk(await authenticatedFetch(`${baseUrl}/notifications/preferences`));

    return response.json();
};

export const updateNotificationPreferences = async (
    preferences: NotificationPreferences,
): Promise<NotificationPreferences> => {
    const response = await sendJson("PUT", `${baseUrl}/notifications/preferences`, preferences);

    return (await response.json().catch(() => null)) ?? preferences;
};
